import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import type { z } from 'zod/v4';
import { RecoverySettingsSchema } from './use-load-recovery-settings';

const URL = '/recovery_settings';

const UpdateRecoverySettingsVariablesSchema = RecoverySettingsSchema.partial();

export type UpdateRecoverySettingsVariables = z.infer<typeof UpdateRecoverySettingsVariablesSchema>;

export async function updateRecoverySettingsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateRecoverySettingsVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateRecoverySettingsVariablesSchema.parse(variables));
}

export function useUpdateRecoverySettings() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateRecoverySettingsVariables>({
    mutationFn: variables => updateRecoverySettingsFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['recoverySettings'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
      // Reminder mode and default templates are still read from the legacy
      // configuration by the reminders, invoice and batch-action screens.
      client.invalidateQueries({ queryKey: ['company-configuration'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateRecoverySettingsVariables,
    'updateRecoverySettings'
  >('updateRecoverySettings', mutation);
}
